import { useIsMutating, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { ConversationStatusEnum } from '@/Domain/enum/ConversationStatus.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useChangeConversationStatus } from '@/Domain/hooks/Conversation/useChangeConversationStatus.hook';

export const useConversationSendMedia = (conversation: ConversationDto) => {
  const { handleChangeStatus, canChangeStatus } = useChangeConversationStatus(conversation);
  const { capture } = usePostHogAnalytics();
  const mutationKey = [QueryKey.conversation.sendMedia, conversation.id];
  const pendingMutationCount = useIsMutating({ mutationKey, exact: true });

  const mutation = useMutation<
    unknown,
    unknown,
    { conversationId: string; file: File; caption?: string }
  >({
    mutationKey,
    onSuccess: () => {
      capture(PostHogEventEnum.HumanTakeoverInitiated, {
        conversation_id: conversation.id,
        source: conversation.source,
      });

      if (conversation.resolution_status === ConversationStatusEnum.Resolved && canChangeStatus) {
        handleChangeStatus(ConversationStatusEnum.Open);
      }
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      const message =
        error.response?.data?.errors?.root?.[0]?.message ||
        i18n.t('inbox_v2:conversation.attachment_upload_failed');
      toast.error(message);
    },
  });

  const handleSendMedia = (file: File, caption?: string) => {
    mutation.mutate({ conversationId: conversation.id, file, caption });
  };

  return { handleSendMedia, isPending: mutation.isPending || pendingMutationCount > 0 };
};
